import { htmlToJson } from './common';
import { diffChars } from 'diff';

/**
 * Returns true when the HTML string contains WordPress block comment delimiters
 * (e.g. `<!-- wp:group -->`). Used to decide whether to bypass `htmlToJson()`
 * (which strips HTML comments) and to skip re-conversion in PageEditorContainer.
 *
 * Defined here (not in lp-block-converter.ts) to avoid pulling in @wordpress/blocks
 * transitively for non-Gutenberg pages.
 */
export function isGutenbergBlockContent(html: string | undefined | null): boolean {
  return !!html && /<!--\s*wp:/.test(html);
}

/**
 * Count the Carbon Fields custom blocks in an HTML string.
 *
 * Counts raw `<!-- wp:carbon-fields/* -->` comments AND our own
 * `<!-- wp:quattr/cb-placeholder -->` wrappers (which carry an original Carbon
 * Fields block in their attributes) — content is CF-preserving in either form.
 *
 * Used by the publish guard: a page whose CMS content contains Carbon Fields
 * blocks must never be published with NONE of them — total loss is the
 * signature of a corrupted/flattened editor state (e.g. the editor was seeded
 * from a stale non-block document), and publishing it wipes the customer's
 * special blocks (empty excerpt slot, theme formatting loss on Simpplr-style
 * themes).
 */
export function countCarbonFieldsBlocks(html: string | undefined | null): number {
  if (!html) return 0;
  return (String(html).match(/<!--\s*wp:(?:carbon-fields\/|quattr\/cb-placeholder)/g) || []).length;
}

/**
 * Per-type Carbon Fields block counts, e.g. { "nn-blog-excerpt": 1, "nn-blog-image": 8 }.
 *
 * Counts raw `<!-- wp:carbon-fields/<type> -->` comments, plus `quattr/cb-placeholder`
 * wrappers by the real type carried in their `blockType` attribute. A placeholder whose
 * real type can't be read is counted under "quattr/cb-placeholder".
 *
 * Needed because the total count alone is not a safe publish guard: the Simpplr theme
 * renders broken HTML (an unclosed <strong> that bolds the whole page) when a glossary
 * post loses its nn-blog-excerpt block, even while all 8 nn-blog-image blocks survive —
 * so the guard must trip on the loss of any block TYPE the CMS page has, not only on
 * losing every Carbon Fields block.
 */
export function carbonFieldsBlockTypeCounts(html: string | undefined | null): Record<string, number> {
  const counts: Record<string, number> = {};
  if (!html) return counts;
  const s = String(html);
  const add = (type: string) => {
    counts[type] = (counts[type] || 0) + 1;
  };
  for (const m of s.matchAll(/<!--\s*wp:carbon-fields\/([a-zA-Z0-9_-]+)/g)) add(m[1]);
  // cb-placeholder wrappers: read the real type from the blockType attribute inside the SAME
  // comment (the tempered scan cannot cross a comment terminator, so a placeholder without a
  // readable blockType can't steal one from a later block). Legacy MULTIPLY-WRAPPED placeholders
  // (blockType is itself "quattr/cb-placeholder" — see resolveCbPlaceholder in common.js) still
  // carry the innermost real type literally inside their escaped originalValue ("carbon-fields/"
  // is plain ASCII, untouched by the \uXXXX comment escaping), so fall back to scanning the whole
  // comment body for it before bucketing the placeholder as opaque.
  for (const m of s.matchAll(/<!--\s*wp:quattr\/cb-placeholder\s+((?:(?!-->)[\s\S])*?)\/?-->/g)) {
    const typeMatch =
      m[1].match(/"blockType"\s*:\s*"carbon-fields\/([a-zA-Z0-9_-]+)"/) ||
      m[1].match(/carbon-fields\/([a-zA-Z0-9_-]+)/);
    if (typeMatch) {
      add(typeMatch[1]);
      continue;
    }
    // A placeholder wrapping a non-Carbon block (preserve-unsupported-blocks also wraps core
    // reusable blocks as "block" and "pullquote") is definitively NOT Carbon Fields evidence: it
    // must neither create a phantom CF requirement on the original side nor grant the publish
    // guard's missing-type allowance on the outgoing side (an unrelated preserved reusable block
    // could otherwise mask a genuinely missing nn-blog-excerpt). The inner blockType may sit at
    // ANY legacy nesting depth, where each wrap escapes the quotes again (", \\u0022, …) —
    // so decode the serialize_block_attributes escaping first, then inspect every readable
    // blockType value. Only a placeholder with no readable type at all stays in the opaque
    // bucket. (A Carbon Fields inner type was already resolved above and takes priority.)
    const decoded = m[1]
      .replace(/\\+u0022/gi, '"')
      .replace(/\\+u003c/gi, '<')
      .replace(/\\+u003e/gi, '>')
      .replace(/\\+u0026/gi, '&')
      .replace(/\\+u002d/gi, '-');
    const blockTypeValues = [...decoded.matchAll(/"blockType"\s*:\s*"([^"]*)"/g)].map((t) => t[1]);
    const hasReadableNonCfType = blockTypeValues.some(
      (v) => v && v !== 'quattr/cb-placeholder' && !v.startsWith('carbon-fields/')
    );
    if (hasReadableNonCfType) {
      continue;
    }
    add('quattr/cb-placeholder');
  }
  return counts;
}
export interface GutenbergBlock {
  id: string;
  type: string;
  index: string;
  element: HTMLElement;
  content: string;
  innerHTML: string;
  isEditing: boolean;
}

/**
 * Parse HTML content to extract Gutenberg blocks
 */
export const parseGutenbergBlocks = (htmlContent: string): GutenbergBlock[] => {
  if (!htmlContent) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const gutenbergElements = doc.querySelectorAll('[data-gutenberg-block]');

  const blocks: GutenbergBlock[] = [];
  gutenbergElements.forEach((element) => {
    const id = element.getAttribute('data-gutenberg-block') || '';
    const type = element.getAttribute('data-block-type') || '';
    const index = element.getAttribute('data-block-index') || '';

    if (id) {
      blocks.push({
        id,
        type,
        index,
        element: element as HTMLElement,
        content: element.outerHTML,
        innerHTML: element.innerHTML,
        isEditing: false,
      });
    }
  });

  return blocks;
};

/**
 * Check if HTML content has Gutenberg blocks
 */
export const hasGutenbergBlocks = (htmlContent: string): boolean => {
  if (!htmlContent) return false;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const gutenbergElements = doc.querySelectorAll('[data-gutenberg-block]');
  return gutenbergElements.length > 0;
};

/**
 * Update a specific Gutenberg block in HTML content
 */
export const updateGutenbergBlock = (htmlContent: string, blockId: string, updatedBlockContent: string): string => {
  if (!htmlContent || !blockId || updatedBlockContent === undefined) {
    return htmlContent;
  }

  const parser = new DOMParser();
  const mainDoc = parser.parseFromString(htmlContent, 'text/html');

  // Find the block element in the main document
  const blockElement = mainDoc.querySelector(`[data-gutenberg-block="${blockId}"]`);

  if (blockElement) {
    // Update only the innerHTML of the block, preserving all Gutenberg attributes
    // Ensure content is not empty to prevent self-closing tags
    blockElement.innerHTML = updatedBlockContent || '&nbsp;';

    // Force the browser to recognize the content by accessing it
    const serialized = mainDoc.body.innerHTML;

    return serialized;
  }

  return htmlContent;
};

/**
 * Extract content for editing from a Gutenberg block
 */
export const extractBlockContentForEditing = (block: GutenbergBlock): string => {
  if (!block || !block.content) return '';

  // Return inner content without Gutenberg attributes for editing
  return block.innerHTML;
};

/**
 * Get block type display name
 */
export const getBlockTypeDisplayName = (blockType: string): string => {
  const typeMap: Record<string, string> = {
    'core/paragraph': 'Paragraph',
    'core/heading': 'Heading',
    'core/list': 'List',
    'core/image': 'Image',
    'core/quote': 'Quote',
    'core/code': 'Code',
    'core/table': 'Table',
    'core/group': 'Group',
    'core/columns': 'Columns',
    'core/column': 'Column',
    'core/media-text': 'Media & Text',
    'core/gallery': 'Gallery',
    'core/cover': 'Cover',
    'core/embed': 'Embed',
    'core/separator': 'Separator',
    'core/spacer': 'Spacer',
    'core/more': 'More',
    'core/preformatted': 'Preformatted',
    'core/pullquote': 'Pullquote',
    'core/verse': 'Verse',
    'core/button': 'Button',
    'core/buttons': 'Buttons',
  };

  return (
    typeMap[blockType] ||
    blockType
      .replace('core/', '')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

/**
 * Create a new Gutenberg block wrapper
 */
export const createGutenbergBlockWrapper = (content: string, blockType: string, blockIndex: number): string => {
  const blockId = `block_${blockType.replace('core/', '')}_${blockIndex}_${Date.now()}`;

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const element = doc.body.firstElementChild;

  if (element) {
    element.setAttribute('data-gutenberg-block', blockId);
    element.setAttribute('data-block-type', blockType);
    element.setAttribute('data-block-index', blockIndex.toString());

    return element.outerHTML;
  }

  return content;
};

/**
 * Convert regular HTML content to Gutenberg blocks
 */
export const convertToGutenbergBlocks = (htmlContent: string): string => {
  if (!htmlContent) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const elements = doc.body.children;

  let blockIndex = 0;
  Array.from(elements).forEach((element) => {
    // Skip if already a Gutenberg block
    if (element.getAttribute('data-gutenberg-block')) return;

    let blockType = 'core/paragraph';
    const tagName = element.tagName.toLowerCase();

    // Determine block type based on HTML tag
    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
      blockType = 'core/heading';
    } else if (['ul', 'ol'].includes(tagName)) {
      blockType = 'core/list';
    } else if (tagName === 'img') {
      blockType = 'core/image';
    } else if (tagName === 'blockquote') {
      blockType = 'core/quote';
    } else if (tagName === 'pre' || tagName === 'code') {
      blockType = 'core/code';
    } else if (tagName === 'table') {
      blockType = 'core/table';
    }

    const blockId = `block_${blockType.replace('core/', '')}_${blockIndex}_${Date.now()}`;

    element.setAttribute('data-gutenberg-block', blockId);
    element.setAttribute('data-block-type', blockType);
    element.setAttribute('data-block-index', blockIndex.toString());

    blockIndex++;
  });

  return doc.body.innerHTML;
};

/**
 * Remove Gutenberg block attributes from HTML content
 */
export const removeGutenbergAttributes = (htmlContent: string): string => {
  if (!htmlContent) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const elements = doc.querySelectorAll('[data-gutenberg-block]');

  elements.forEach((element) => {
    element.removeAttribute('data-gutenberg-block');
    element.removeAttribute('data-block-type');
    element.removeAttribute('data-block-index');
  });

  return doc.body.innerHTML;
};

/**
 * Insert a new Gutenberg block at a specific position
 */
export const insertGutenbergBlock = (
  htmlContent: string,
  newBlockContent: string,
  blockType: string,
  position: 'start' | 'end' | number
): string => {
  if (!newBlockContent) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent || '<div></div>', 'text/html');
  const existingBlocks = doc.querySelectorAll('[data-gutenberg-block]');

  // Determine the next block index
  const nextIndex = existingBlocks.length;
  const blockId = `block_${blockType.replace('core/', '')}_${nextIndex}_${Date.now()}`;

  // Parse the new block content
  const newBlockDoc = parser.parseFromString(newBlockContent, 'text/html');
  const newElement = newBlockDoc.body.firstElementChild;

  if (newElement) {
    newElement.setAttribute('data-gutenberg-block', blockId);
    newElement.setAttribute('data-block-type', blockType);
    newElement.setAttribute('data-block-index', nextIndex.toString());

    if (position === 'start') {
      // Insert at the beginning
      doc.body.insertBefore(newElement, doc.body.firstChild);
    } else if (position === 'end') {
      // Insert at the end
      doc.body.appendChild(newElement);
    } else if (typeof position === 'number') {
      // Insert at specific index
      const targetBlock = Array.from(existingBlocks)[position];
      if (targetBlock) {
        targetBlock.parentNode?.insertBefore(newElement, targetBlock.nextSibling);
      } else {
        doc.body.appendChild(newElement);
      }
    }

    // Re-index all blocks
    const allBlocks = doc.querySelectorAll('[data-gutenberg-block]');
    allBlocks.forEach((block, index) => {
      block.setAttribute('data-block-index', index.toString());
    });

    return doc.body.innerHTML;
  }

  return htmlContent;
};

/**
 * Delete a Gutenberg block by ID
 */
export const deleteGutenbergBlock = (htmlContent: string, blockId: string): string => {
  if (!htmlContent || !blockId) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const blockElement = doc.querySelector(`[data-gutenberg-block="${blockId}"]`);

  if (blockElement) {
    blockElement.remove();

    // Re-index remaining blocks
    const allBlocks = doc.querySelectorAll('[data-gutenberg-block]');
    allBlocks.forEach((block, index) => {
      block.setAttribute('data-block-index', index.toString());
    });

    return doc.body.innerHTML;
  }

  return htmlContent;
};

/**
 * Move a Gutenberg block to a new position
 */
export const moveGutenbergBlock = (htmlContent: string, blockId: string, direction: 'up' | 'down'): string => {
  if (!htmlContent || !blockId) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const blockElement = doc.querySelector(`[data-gutenberg-block="${blockId}"]`);

  if (blockElement) {
    if (direction === 'up' && blockElement.previousElementSibling) {
      blockElement.parentNode?.insertBefore(blockElement, blockElement.previousElementSibling);
    } else if (direction === 'down' && blockElement.nextElementSibling) {
      blockElement.parentNode?.insertBefore(blockElement.nextElementSibling, blockElement);
    }

    // Re-index all blocks
    const allBlocks = doc.querySelectorAll('[data-gutenberg-block]');
    allBlocks.forEach((block, index) => {
      block.setAttribute('data-block-index', index.toString());
    });

    return doc.body.innerHTML;
  }

  return htmlContent;
};

/**
 * Get block templates for different block types
 */
export const getBlockTemplate = (blockType: string): string => {
  const templates: Record<string, string> = {
    'core/paragraph': '<p>Start writing or type / to choose a block</p>',
    'core/heading': '<h2>Heading</h2>',
    'core/list': '<ul><li>List item 1</li><li>List item 2</li><li>List item 3</li></ul>',
    'core/quote': '<blockquote><p>Quote text</p></blockquote>',
    'core/code': '<pre><code>// Your code here</code></pre>',
    'core/image': '<figure class="wp-block-image"><img src="" alt="" /></figure>',
    'core/table':
      '<table><tbody><tr><td>Cell 1</td><td>Cell 2</td></tr><tr><td>Cell 3</td><td>Cell 4</td></tr></tbody></table>',
    'core/separator': '<hr class="wp-block-separator" />',
    'core/spacer': '<div style="height:50px" aria-hidden="true" class="wp-block-spacer"></div>',
    'core/button': '<div class="wp-block-button"><a class="wp-block-button__link">Button</a></div>',
  };

  return templates[blockType] || '<p>New block</p>';
};

/**
 * Validate Gutenberg block structure
 */
export const validateGutenbergBlocks = (htmlContent: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!htmlContent) {
    return { isValid: true, errors: [] };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const gutenbergElements = doc.querySelectorAll('[data-gutenberg-block]');

  const seenIds = new Set<string>();

  gutenbergElements.forEach((element, index) => {
    const id = element.getAttribute('data-gutenberg-block');
    const type = element.getAttribute('data-block-type');
    const blockIndex = element.getAttribute('data-block-index');

    // Check for required attributes
    if (!id) {
      errors.push(`Block at position ${index} is missing data-gutenberg-block attribute`);
    }
    if (!type) {
      errors.push(`Block at position ${index} is missing data-block-type attribute`);
    }
    if (!blockIndex) {
      errors.push(`Block at position ${index} is missing data-block-index attribute`);
    }

    // Check for duplicate IDs
    if (id) {
      if (seenIds.has(id)) {
        errors.push(`Duplicate block ID found: ${id}`);
      } else {
        seenIds.add(id);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Convert HTML content to WordPress Gutenberg blocks format with block comments
 * This is useful when accepting GIGA content and converting it to Gutenberg editor format
 *
 * @param htmlContent - The HTML content to convert
 * @returns HTML with WordPress block comments (<!-- wp:paragraph --> etc.) suitable for Gutenberg editor
 */
/**
 * Recursively clean GIGA-specific attributes from JSON tree
 */
const cleanGigaAttributesFromJson = (jsonObj: any): any => {
  if (!jsonObj || typeof jsonObj !== 'object') return jsonObj;

  if (Array.isArray(jsonObj)) {
    return jsonObj.map((item) => cleanGigaAttributesFromJson(item));
  }

  const cleaned = { ...jsonObj };

  // Clean attributes object
  if (cleaned.attributes && typeof cleaned.attributes === 'object') {
    const attrs = { ...cleaned.attributes };
    // Remove GIGA-specific attributes
    delete attrs.isGigaSuggested;
    delete attrs.isgigasuggested;
    delete attrs['data-content-tag'];
    // Remove any data-giga-* attributes
    Object.keys(attrs).forEach((key) => {
      if (key.startsWith('data-giga-')) {
        delete attrs[key];
      }
    });
    cleaned.attributes = attrs;
  }

  // Recursively clean children
  if (cleaned.children && Array.isArray(cleaned.children)) {
    cleaned.children = cleaned.children.map((child: any) => cleanGigaAttributesFromJson(child));
  }

  return cleaned;
};

export const convertHtmlToGutenbergFormat = (htmlContent: string): string => {
  if (!htmlContent) return '';

  try {
    // First, remove GIGA-specific attributes from HTML string
    // This catches any attributes that might not be parsed into the JSON structure
    let cleanedHtml = htmlContent
      .replace(/\s*isGigaSuggested="[^"]*"/g, '') // Remove isGigaSuggested (camelCase)
      .replace(/\s*isgigasuggested="[^"]*"/gi, '') // Remove isgigasuggested (any case)
      .replace(/\s*data-giga-suggestion="[^"]*"/gi, '')
      .replace(/\s*data-giga-[^=]*="[^"]*"/gi, '')
      .replace(/\s*data-content-tag="[^"]*"/gi, ''); // Remove data-content-tag

    // Convert HTML to JSON
    let jsonContent = htmlToJson(cleanedHtml);

    if (!jsonContent) return cleanedHtml;

    // Clean GIGA attributes from the JSON tree structure
    // This is important because htmlToJson might preserve these attributes in the JSON
    jsonContent = cleanGigaAttributesFromJson(jsonContent);

    // Import jsonToWPHtml dynamically to avoid circular dependencies
    // jsonToWPHtml adds WordPress block comments and formats the content properly
    const { jsonToWPHtml } = require('./common');

    // Convert JSON to WordPress HTML format with block comments
    const wpFormattedHtml = jsonToWPHtml(jsonContent);

    return wpFormattedHtml || cleanedHtml;
  } catch (error) {
    console.error('Error converting HTML to Gutenberg format:', error);
    return htmlContent;
  }
};

export const calculateRefreshPercentage = (oldHtml: string, newHtml: string): number => {
  const oldText = stripHtml(oldHtml);
  const newText = stripHtml(newHtml);

  if (!oldText && !newText) return 0;
  if (!oldText) return 100;

  const changes = diffChars(oldText, newText);

  let changed = 0;
  let total = 0;

  changes.forEach((part: { value: string; added?: boolean; removed?: boolean }) => {
    total += part.value.length;
    if (part.added || part.removed) {
      changed += part.value.length;
    }
  });

  return total === 0 ? 0 : Math.round((changed / total) * 100);
};

const stripHtml = (html: string): string => {
  return (
    html
      ?.replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || ''
  );
};
